// react
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

// components
import {
  Input,
  BUTTON_THEME,
  Select,
  EntitySelect,
  CircularLoader,
} from '@st-operations/uikit/src/v2/index.js'
import User from '../../../lib/User'
import Header from '_components/header/index.jsx'

//hooks
import { useRetractSidebar } from '_hooks'

// lib
import pretty from '_root/lib/pretty.js'
import sendAnalytics from '_root/lib/sendAnalytics.js'

// services
import { loadSchema, loadEntity, updateEntity, uploadS3 } from '_services/content-rule'

// local
import Tokens from './Tokens.jsx'
import { FIELDS, INITIAL_STATE } from './constants.js'
import { logErrorAsync } from '../../../lib/utility'

const body = document.querySelector('body')

const ENTITY = 'content-rules'

const ContentRulePage = (props) => {
  const { id } = props.match.params

  const [data, setData] = useState()
  const [schema, setSchema] = useState()
  const [values, setValues] = useState(INITIAL_STATE)
  const tokensRef = useRef({ baseTokens: [], addedTokens: [], removedTokens: [] })

  const [hasChanges, setHasChanges] = useState(false)
  const [inRequest, setInRequest] = useState(false)

  useRetractSidebar()

  const load = async (id) => {
    try {
      const [schema, data] = await Promise.all([loadSchema(ENTITY), loadEntity(ENTITY, id)])
      setSchema(schema)
      setData(data)
      sendAnalytics('read', ENTITY, data)
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    }
  }

  useEffect(() => {
    load(id)
  }, [id])

  useEffect(() => {
    if (data) {
      setValues((initialState) =>
        Object.fromEntries(Object.entries(initialState).map(([key]) => [key, data[key]]))
      )
    }
  }, [data])

  const handleSave = async () => {
    if (inRequest) return

    const currentTokens = tokensRef.current || {}
    const baseTokens = currentTokens.baseTokens || []
    const addedTokens = currentTokens.addedTokens || []

    if (currentTokens.validate && !currentTokens.validate()) {
      return
    }

    setInRequest('Updating')
    const { _id, email } = User.getProfile()

    const newData = {
      ...data,
      ...values,
      tokens: [...baseTokens, ...addedTokens].join('\n'),
      updatedAt: new Date().toISOString(),
      updatedBy: { _id, email },
    }

    try {
      await updateEntity(ENTITY, id, newData)
      toast.success('Update successful!')
      toast('Distributing changes to s3...')
      uploadS3(ENTITY)
        .then(() => toast.success('Changes distributed to s3!'))
        .catch((error) => toast.error(error.message))
      handleExit()
    } catch (error) {
      if (error.code === 400 && error.message?.content_rule_change_request?._id) {
        //redirect to change requests page with a relevant filter
        props.history.push(`/content-rule-change-requests?created_by.email=${email}&status=pending`)
        return
      }
      logErrorAsync(error)
      toast.error(error.message)
      setInRequest(false)
    }

    sendAnalytics('update', ENTITY, newData)
  }

  const handleChange = (field, value) => {
    setValues((v) => ({ ...v, [field.name]: value }))
    setHasChanges(true)
  }

  const handleExit = () => {
    props.history.push(`/${ENTITY}`)
  }

  const headerActions = [
    {
      children: 'Exit',
      onClick: handleExit,
      theme: BUTTON_THEME.GHOST,
    },
    {
      children: 'Save',
      onClick: handleSave,
      disabled: !hasChanges,
    },
  ]

  if (!(data && schema)) {
    return (
      <div className="content-rule-loading">
        <CircularLoader />
      </div>
    )
  }

  return (
    <div id="content-rule-page" className="content-rule">
      <If condition={inRequest}>
        {createPortal(
          <div className="content-rule__updating-wrapper">
            <div className="content-rule__updating-content">
              <p className="content-rule__updating-message">{inRequest}</p>
              <CircularLoader />
            </div>
          </div>,
          body
        )}
      </If>
      <Header actions={headerActions} back={handleExit} title={values.name} />
      <div className="content-rule__fields-wrapper">
        <For each="field" of={FIELDS}>
          <Choose>
            <When condition={field.type === 'text'}>
              <Input
                key={field.name}
                name={field.name}
                label={field.label ?? pretty(field.name)}
                value={values[field.name]}
                onChange={(value) => handleChange(field, value)}
                description={schema[field.name]?.description ?? ''}
                containerStyle={{ gridColumn: field.position }}
              />
            </When>
            <When condition={field.type === 'select'}>
              <Select
                key={field.name}
                name={field.name}
                label={field.label ?? pretty(field.name)}
                options={(schema[field.name].enum || []).filter(
                  (option) => !values[field.name].includes(option)
                )}
                value={
                  Array.isArray(values[field.name]) ? values[field.name] : [values[field.name]]
                }
                onChange={(_, _2, newValues) => handleChange(field, newValues)}
                description={schema[field.name]?.description ?? ''}
                style={{ gridColumn: field.position }}
                isMulti={schema[field.name].multi}
                useReactSelect={true}
                isSearchable={true}
              />
            </When>
            <When condition={field.type === 'entity-select'}>
              <div key={field.name} style={{ gridColumn: field.position }}>
                <EntitySelect
                  name={field.name}
                  fields={field?.fields || null}
                  entity={schema[field.name].collection}
                  value={
                    Array.isArray(values[field.name]) ? values[field.name] : [values[field.name]]
                  }
                  label={field.label ?? pretty(field.name)}
                  isMulti={schema[field.name].multi}
                  onChange={(_, _2, newValues) => handleChange(field, newValues)}
                  isClearable={false}
                />
              </div>
            </When>
          </Choose>
        </For>
      </div>
      <div>
        <Tokens
          tokens={values.tokens ?? ''}
          tokensRef={tokensRef}
          setHasChanges={setHasChanges}
          tokenType={values.tokenType}
        />
      </div>
    </div>
  )
}

export default ContentRulePage
